import { formatDate, plural } from '@/lib/format'
import { dietLabels } from '@/lib/labels'
import type { Diet, WeddingData } from '@/mock/types'
import { taskPromptState } from '@/patterns/prompts'

export type GuestPromptAction =
  | { kind: 'task'; title: string }
  | { kind: 'share' }
  | { kind: 'settings'; label: string }

export type GuestPromptRule = {
  code: string
  title: string
  hint: string
  action: GuestPromptAction
}

/**
 * "Na co nezapomenout" for Hosté, from the guest list itself: who hasn't
 * replied, diets, accommodation, transport, children; plus two general tips.
 * A task prompt disappears once its task is done.
 */
export function guestPrompts(
  data: WeddingData,
): (GuestPromptRule & { state: 'open' | 'in-tasks' })[] {
  const people = data.parties.flatMap((party) => party.persons)
  const coming = people.filter((person) => person.attendance !== 'declined')
  const pending = people.filter(
    (person) => person.attendance === 'invited' || person.attendance === 'waiting',
  ).length
  const comingParties = data.parties.filter((party) =>
    party.persons.some((person) => person.attendance !== 'declined'),
  )
  const accommodation = comingParties.filter((party) => party.accommodation).length
  const transport = comingParties.filter((party) => party.transport).length
  const children = coming.filter((person) => person.child).length
  const dietCounts = new Map<Diet, number>()
  for (const person of coming)
    for (const diet of person.diets) dietCounts.set(diet, (dietCounts.get(diet) ?? 0) + 1)
  const withDiet = coming.filter((person) => person.diets.length > 0).length
  const deadline = data.website.rsvpDeadline

  const rules: (GuestPromptRule | false)[] = [
    pending > 0 &&
      data.website.published && {
        code: 'guests-reminder',
        title: 'Kdo ještě neodpověděl?',
        hint: `${pending} ${guests(pending)} bez odpovědi${deadline ? `, termín je ${formatDate(deadline, 'long')}` : ''}.`,
        action: { kind: 'share' },
      },
    !deadline && {
      code: 'guests-deadline',
      title: 'Do kdy mají hosté odpovědět?',
      hint: 'Bez termínu se odpovědi táhnou do poslední chvíle.',
      action: { kind: 'settings', label: 'Nastavit termín' },
    },
    withDiet > 0 && {
      code: 'guests-diets',
      title: 'Ví kuchyně o dietách?',
      hint: `${withDiet} ${guests(withDiet)} s dietou: ${[...dietCounts]
        .map(([diet, count]) => `${count}× ${dietLabels[diet].toLowerCase()}`)
        .join(', ')}.`,
      action: { kind: 'task', title: 'Poslat cateringu přehled diet' },
    },
    accommodation > 0 && {
      code: 'guests-accommodation',
      title: 'Kde budou hosté spát?',
      hint: `${invitations(accommodation)} ${plural(accommodation, 'chce', 'chtějí', 'chce')} ubytování.`,
      action: { kind: 'task', title: 'Zajistit ubytování pro hosty' },
    },
    transport > 0 && {
      code: 'guests-transport',
      title: 'Kdo odveze hosty?',
      hint: `${invitations(transport)} ${plural(transport, 'potřebuje', 'potřebují', 'potřebuje')} odvoz.`,
      action: { kind: 'task', title: 'Domluvit odvoz hostů' },
    },
    children > 0 && {
      code: 'guests-children',
      title: 'Na co nezapomenout kvůli dětem?',
      hint: `${children} ${plural(children, 'dítě', 'děti', 'dětí')}: dětské menu, židličky, hlídání nebo koutek na hraní.`,
      action: { kind: 'task', title: 'Připravit menu a program pro děti' },
    },
    people.length > 0 && {
      code: 'guests-final-count',
      title: 'Kdy nahlásíte finální počet hostů?',
      hint: 'Catering ho většinou chce dva až tři týdny předem.',
      action: { kind: 'task', title: 'Nahlásit finální počet hostů cateringu' },
    },
    people.length > 0 && {
      code: 'guests-accessibility',
      title: 'Potřebuje někdo bezbariérový přístup?',
      hint: 'Babičky, dědečkové nebo hosté na vozíku: schody, parkování, toalety.',
      action: { kind: 'task', title: 'Ověřit bezbariérový přístup pro hosty' },
    },
  ]

  const hidden = new Set(data.hiddenPrompts)
  return rules.flatMap((rule) => {
    if (!rule || hidden.has(rule.code)) return []
    if (rule.action.kind !== 'task') return [{ ...rule, state: 'open' as const }]
    const state = taskPromptState(data.tasks, rule.action.title)
    return state === 'done' ? [] : [{ ...rule, state }]
  })
}

const guests = (count: number) => plural(count, 'host', 'hosté', 'hostů')
const invitations = (count: number) =>
  `${count} ${plural(count, 'pozvánka', 'pozvánky', 'pozvánek')}`
